import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { JustRightHeightModal } from 'components/styled/scoped';
import { css } from '@emotion/css';
import { Alert, Form as AntForm, Input, Switch, Typography } from 'antd';
import { Formik } from 'formik';
import { useTranslate } from '@tolgee/react';
import { tolgee } from 'services/i18n/tolgee';
import type { KnowledgeId, ProductKnowledgeScopeSnapshot } from '../../model/types';
import { DocumentScopeEditor } from '../DocumentScopeEditor';
import { FormDiscardGuard, useConfirmBeforeDiscard } from '../FormDiscardGuard';

export interface KnowledgeEditValues {
  answer: string;
  scope: ProductKnowledgeScopeSnapshot;
  /** 仅 showEnableToggle 场景参与提交，其余消费方不感知 */
  enabled?: boolean;
}

export interface KnowledgeEditModalProps {
  visible: boolean;
  question: string;
  initialValues: KnowledgeEditValues;
  onSubmit: (values: KnowledgeEditValues) => Promise<void>;
  onCancel: () => void;
  scopeEditable?: boolean;
  /** 作用范围编辑形态：document=外部文档知识，products=商详页知识 */
  scopeVariant?: 'products' | 'document';
  /** 覆盖 scope 编辑器使用的知识类型（客服接待知识等特殊类型用） */
  scopeKnowledgeType?: 'SPU_DETAIL_KNOWLEDGE' | 'DOCUMENT_KNOWLEDGE' | 'SUPPLEMENTAL_KNOWLEDGE';
  /** 客服接待知识的 scope.expand 是只读投影，不展示关联模式开关 */
  showScopeMode?: boolean;
  /** 会话引用场景允许在编辑时直接启停知识，随保存一并提交 */
  showEnableToggle?: boolean;
  knowledgeId?: KnowledgeId;
  title?: string;
}

const modalClassName = css`
  .knowledge-edit-readonly {
    max-height: 96px;
    padding: 8px 12px;
    overflow-y: auto;
    border-radius: 4px;
    background: var(--fett-bg-secondary, #f5f5f5);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .knowledge-edit-answer {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

// 详情数据可能缺 scope/skus 字段（对齐 perseus 的可选 scopeSnapshot），缺参时兜底空快照
const cloneInitialValues = (values: KnowledgeEditValues): KnowledgeEditValues => ({
  answer: values.answer,
  enabled: values.enabled ?? true,
  scope: {
    expand: values.scope?.expand ?? false,
    spus: (values.scope?.spus ?? []).map((item) => ({
      ...item,
      selectedSkuKeys: [...(item.selectedSkuKeys ?? [])],
    })),
  },
});

const validate = ({ answer }: KnowledgeEditValues) => {
  if (!answer.trim()) return { answer: tolgee.t('productKnowledge_edit_answer_required') };
  if (answer.length > 10000) return { answer: tolgee.t('productKnowledge_edit_answer_too_long') };
  return {};
};

export function KnowledgeEditModal({
  visible,
  question,
  initialValues,
  onSubmit,
  onCancel,
  scopeEditable = true,
  scopeVariant = 'products',
  scopeKnowledgeType,
  showScopeMode = true,
  showEnableToggle = false,
  knowledgeId,
  title,
}: KnowledgeEditModalProps) {
  const { t } = useTranslate();
  const modalTitle = title ?? t('productKnowledge_edit_modal_title');
  const confirmCancel = useConfirmBeforeDiscard(onCancel);

  return (
    <Formik<KnowledgeEditValues>
      enableReinitialize
      initialValues={cloneInitialValues(initialValues)}
      validate={validate}
      onSubmit={async (values, { setStatus }) => {
        setStatus(undefined);
        try {
          // enabled 仅在展示启停开关的场景随表单提交，其余消费方不感知
          const payload: KnowledgeEditValues = showEnableToggle
            ? values
            : { answer: values.answer, scope: values.scope };
          await onSubmit({ ...payload, answer: payload.answer.trim() });
          onCancel();
        } catch (error) {
          setStatus(
            describeKnowledgeApiError(error, undefined, t('productKnowledge_edit_save_failed')),
          );
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        isSubmitting,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => (
        <>
          <FormDiscardGuard id="product-knowledge-edit" active={visible} />
          <JustRightHeightModal
            centered
            className={modalClassName}
            visible={visible}
            title={modalTitle}
            width={760}
            destroyOnClose
            maskClosable={!isSubmitting}
            closable={!isSubmitting}
            cancelButtonProps={{ disabled: isSubmitting }}
            okButtonProps={{ loading: isSubmitting }}
            okText={t('productKnowledge_common_save')}
            cancelText={t('productKnowledge_common_cancel')}
            onCancel={confirmCancel}
            onOk={() => {
              if (!isSubmitting) handleSubmit();
            }}
          >
            <AntForm layout="vertical">
              {status ? <Alert className="!mb-4" type="error" showIcon message={status} /> : null}
              <AntForm.Item label={t('productKnowledge_common_question')}>
                <Typography.Paragraph
                  className="knowledge-edit-readonly"
                  data-testid="knowledge-question"
                >
                  {question.trim() || '-'}
                </Typography.Paragraph>
              </AntForm.Item>
              <AntForm.Item
                label={t('productKnowledge_common_answer')}
                required
                validateStatus={touched.answer && errors.answer ? 'error' : undefined}
                help={touched.answer ? errors.answer : undefined}
              >
                <Input.TextArea
                  className="knowledge-edit-answer"
                  aria-label={t('productKnowledge_common_answer')}
                  name="answer"
                  value={values.answer}
                  autoSize={{ minRows: 4, maxRows: 10 }}
                  maxLength={10000}
                  showCount
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </AntForm.Item>
              {showEnableToggle ? (
                <AntForm.Item label={t('productKnowledge_common_enable')}>
                  <Switch
                    data-testid="knowledge-enable-toggle"
                    checked={values.enabled}
                    disabled={isSubmitting}
                    onChange={(checked) => setFieldValue('enabled', checked)}
                  />
                </AntForm.Item>
              ) : null}
              {scopeEditable ? (
                <AntForm.Item>
                  <DocumentScopeEditor
                    key={`${scopeKnowledgeType ?? scopeVariant}:${knowledgeId}`}
                    knowledgeType={
                      scopeKnowledgeType ??
                      (scopeVariant === 'document' ? 'DOCUMENT_KNOWLEDGE' : 'SPU_DETAIL_KNOWLEDGE')
                    }
                    showScopeMode={showScopeMode}
                    knowledgeId={knowledgeId ?? ''}
                    value={values.scope}
                    disabled={isSubmitting}
                    onChange={() => undefined}
                  />
                </AntForm.Item>
              ) : null}
            </AntForm>
          </JustRightHeightModal>
        </>
      )}
    </Formik>
  );
}
